// src/utils/nudgeMessage.js
// D-297 v1.1 — the WhatsApp nudge on Team Activity (Ravi 4 Nov 2026).
//
// WHY THIS EXISTS: on an amber / red row (and for "Never logged in") the Owner
// can open WhatsApp with a ready message to that person — in Roman Hinglish
// (D-307 field language), saying what Zeno expects from their role and since
// how many working days nothing came in. The page only renders the link; the
// number rule, the text and the URL encoding live here, with Jest.
//
// Rules:
//   - The number comes ONLY from normaliseIndianMobile() (phoneIn.js, the same
//     rule as AddUser / EditUser) and must be a mobile (starts 6–9). Anything
//     else = no number = no button. Never a guessed number.
//   - The role rule is activityMap.js's bandRoleOf() (trimmed, lower-cased,
//     "tech" = technician, unknown = manager), so the message and the colour
//     always talk about the same role.
//   - A wa.me link only OPENS WhatsApp with the text filled in. Nothing is sent
//     by Zeno; the Owner presses Send in WhatsApp.
//   - It is the Owner's own message: signed "— {Owner name}, {Company name}"
//     (signOff()), never as Zeno or a bot.
// Pure: no Firestore, no window. The page passes appUrl (window.location.origin)
// so dev links to dev.zenotech.app and prod to app.zenotech.app.

import { normaliseIndianMobile } from "./phoneIn";
import { WINDOW_DAYS, bandRoleOf } from "./activityMap";

export const NUDGE_KIND = Object.freeze({ SLOW: "slow", NEVER_LOGGED_IN: "never_logged_in" });

/** "91XXXXXXXXXX" for a valid Indian mobile, else null. */
export const waNumber = (phone) => {
  const r = normaliseIndianMobile(phone);
  if (!r.ok || r.empty) return null;
  const digits = r.phone.replace(/^\+91/, "");
  if (!/^[6-9]\d{9}$/.test(digits)) return null; // landline / not a mobile
  return `91${digits}`;
};

/**
 * D-297 8b: the number to WhatsApp a user on — the contact-only
 * `whatsapp_phone` (8a) when it is a valid mobile, else the login `phone` when
 * that is a valid mobile, else "" (no button). Per number (Ravi 4 Nov): a
 * stored but invalid WhatsApp number falls back to the login phone.
 * Returns the stored value as it is (waLink() / nudgeLink() normalise it).
 */
export const contactPhoneOf = (user) => {
  if (waNumber(user?.whatsapp_phone)) return String(user.whatsapp_phone).trim();
  if (waNumber(user?.phone)) return String(user.phone).trim();
  return "";
};

// What Zeno expects from each role — keyed by bandRoleOf(). Texts: Ravi 4 Nov (7b).
const ROLE_LINES = Object.freeze({
  technician:    "Har job Zeno mein Start aur Close karein.",
  site_engineer: "Site ka material Zeno se MRF bana kar hi mangwayein.",
  store:         "Maal aane par GRN aur har issue Zeno mein hi karein.",
  accounts:      "Vendor bill aur payment Zeno mein hi daalein.",
  manager:       "Pending approvals (MRF, PO, expense) Zeno mein approve karein.",
});
const roleLine = (role) => ROLE_LINES[bandRoleOf(role)] || ROLE_LINES.manager;

const greeting = (name) => {
  const n = String(name ?? "").trim();
  return n ? `Hi ${n},` : "Hi,";
};

// The number is WORKING days (Sundays not counted), written as "din" (7b).
const daysPhrase = (workingDays) => {
  if (workingDays === null || workingDays === undefined || !Number.isFinite(Number(workingDays))) {
    return `pichhle ${WINDOW_DAYS} se zyada din se`;
  }
  const n = Math.max(1, Math.floor(Number(workingDays)));
  return `pichhle ${n} din se`;
};

// CompanyContext's fallback when the company doc cannot be read — not a real name.
const PLACEHOLDER_COMPANY_NAMES = ["my company"];
const isPlaceholderCompany = (n) => PLACEHOLDER_COMPANY_NAMES.includes(String(n ?? "").trim().toLowerCase());

/**
 * The company name to sign with: the company doc's company_name (CompanyContext),
 * unless it is missing or the "My Company" placeholder — then the logged-in
 * user's company_name (same fallback as Header / InvoiceDetail; SuperAdmin
 * copies it onto the owner's users doc). Ravi 4 Nov.
 */
export const pickCompanyName = (contextName, userCompanyName) => {
  const ctx = String(contextName ?? "").trim();
  if (ctx && !isPlaceholderCompany(ctx)) return ctx;
  const fromUser = String(userCompanyName ?? "").trim();
  return fromUser && !isPlaceholderCompany(fromUser) ? fromUser : "";
};

/**
 * The sign-off (Ravi 4 Nov): the message goes from the Owner's OWN WhatsApp, so
 * it is signed by the Owner and the company — never as Zeno or a bot.
 *   name + company → "— Ravi, Om Aircon"; name only → "— Ravi";
 *   company only → "— Om Aircon"; neither → "" (no sign-off).
 */
export const signOff = ({ senderName, companyName } = {}) => {
  const name = String(senderName ?? "").trim();
  let company = String(companyName ?? "").trim();
  if (isPlaceholderCompany(company)) company = "";
  const parts = [name, company].filter(Boolean);
  return parts.length ? `— ${parts.join(", ")}` : "";
};

const signature = (args) => {
  const s = signOff(args);
  return s ? `\n\n${s}` : "";
};

/**
 * The message text.
 * @param {Object} a
 * @param {string} a.kind          NUDGE_KIND.SLOW | NUDGE_KIND.NEVER_LOGGED_IN
 * @param {string} a.role          the user's role (any case; unknown = manager's text)
 * @param {string} a.name          the user's name
 * @param {number|null} a.workingDays  working days since the last action; null = nothing in the window
 * @param {string} a.appUrl        required for NEVER_LOGGED_IN (window.location.origin)
 * @param {string} a.senderName    the Owner's name (optional)
 * @param {string} a.companyName   the company's name (optional; "My Company" placeholder ignored)
 */
export const nudgeText = ({ kind, role, name, workingDays, appUrl, senderName, companyName } = {}) => {
  const sign = signature({ senderName, companyName });
  if (kind === NUDGE_KIND.SLOW) {
    return `${greeting(name)}\n${daysPhrase(workingDays)} Zeno mein aapki koi entry nahi aayi. ` +
      `${roleLine(role)} Koi dikkat ho to batayein.${sign}`;
  }
  if (kind === NUDGE_KIND.NEVER_LOGGED_IN) {
    const url = String(appUrl ?? "").trim();
    if (!url) throw new Error("appUrl is required for the never-logged-in message");
    return `${greeting(name)}\naapka Zeno account ready hai. Is link se login karein: ${url} (OTP / password). ` +
      `Koi dikkat ho to batayein.${sign}`;
  }
  throw new Error(`Unknown nudge kind: ${kind}`);
};

/** https://wa.me/91XXXXXXXXXX?text=… — null when the phone is not a valid mobile. */
export const waLink = (phone, text) => {
  const number = waNumber(phone);
  if (!number) return null;
  const t = String(text ?? "");
  return t ? `https://wa.me/${number}?text=${encodeURIComponent(t)}` : `https://wa.me/${number}`;
};

/** The page's one call: { href, text } or null (no valid mobile = no button). */
export const nudgeLink = ({ phone, ...textArgs } = {}) => {
  if (!waNumber(phone)) return null;
  const text = nudgeText(textArgs);
  return { href: waLink(phone, text), text };
};
